import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  detectFromBrowser,
  getCachedCurrency,
  getCachedLocale,
  getCachedCountry,
  setCachedCurrency,
  setCachedLocale,
  setCachedCountry,
  getCurrencyForCountry,
} from '../utils/currency-detection';

interface CurrencyContextType {
  currency: string;
  locale: string;
  country: string;
  setCurrency: (currency: string) => void;
  setLocale: (locale: string) => void;
  setCountry: (country: string) => void;
  updateCurrencyFromCountry: (country: string) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
  initialCurrency?: string;
  initialLocale?: string;
  initialCountry?: string;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
  children,
  initialCurrency,
  initialLocale,
  initialCountry,
}) => {
  const [currency, setCurrencyState] = useState<string>(() => {
    return initialCurrency || getCachedCurrency();
  });
  const [locale, setLocaleState] = useState<string>(() => {
    return initialLocale || getCachedLocale();
  });
  const [country, setCountryState] = useState<string>(() => {
    return initialCountry || getCachedCountry();
  });

  // Initialize from browser on mount
  useEffect(() => {
    const detected = detectFromBrowser();
    
    // Only update if not explicitly provided
    if (!initialCurrency) {
      setCurrencyState(detected.currency);
    }
    if (!initialLocale) {
      setLocaleState(detected.locale);
    }
    if (!initialCountry) {
      setCountryState(detected.country);
    }
  }, []); // Run once on mount

  const setCurrency = (newCurrency: string) => {
    const upperCurrency = newCurrency.toUpperCase();
    setCurrencyState(upperCurrency);
    setCachedCurrency(upperCurrency);
  };

  const setLocale = (newLocale: string) => {
    setLocaleState(newLocale);
    setCachedLocale(newLocale);
  };

  const setCountry = (newCountry: string) => {
    const upperCountry = newCountry.toUpperCase();
    setCountryState(upperCountry);
    setCachedCountry(upperCountry);
    
    // Auto-update currency based on country
    const countryCurrency = getCurrencyForCountry(upperCountry);
    if (countryCurrency !== currency) {
      setCurrency(countryCurrency);
    }
  };

  const updateCurrencyFromCountry = (newCountry: string) => {
    setCountry(newCountry);
  };

  const value: CurrencyContextType = {
    currency,
    locale,
    country,
    setCurrency,
    setLocale,
    setCountry,
    updateCurrencyFromCountry,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};


